import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './store'
import { Participants, Session } from 'src/app/types'
import { addBadge, setCount } from 'src/utils/helpers'
import { appApi } from './appApiQueries'
import { BadgeName } from 'src/app/types'
interface IncrementBadge {
  screenName: string
  name: BadgeName
}
interface SetBadgeCount extends IncrementBadge {
  count: number
}

export const initialState: Session = {
  participants: {},
  totalBadges: 0,
  allBadgesCounts: {} as Session['allBadgesCounts'],
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setName: (state, { payload }: PayloadAction<Session['name']>) => {
      state.name = payload
    },
    setGroupId: (state, { payload }: PayloadAction<Session['groupId']>) => {
      state.groupId = payload
    },
    addParticipants: (state, { payload }: PayloadAction<Participants>) => {
      // add new participants to those that already exist
      // if the participant exists, don't double add (add already existing participants after the newly added ones)
      state.participants = { ...payload, ...state.participants }
    },
    incrementBadge: (
      state,
      { payload: { screenName, name } }: PayloadAction<IncrementBadge>
    ) => {
      // we must go deep within a participant to find their badges
      const participant = state.participants?.[screenName]
      if (participant) {
        state.participants[screenName].badges = addBadge({
          currentBadges: participant.badges,
          name,
        }).sort((a, b) => b.count - a.count) // sort the badges here instead within components
        // update the total badges
        state.totalBadges++
        // add to the all badge counts or start a new entry
        state.allBadgesCounts[name] = (state.allBadgesCounts?.[name] || 0) + 1
      }
    },
    setBadgeCount: (
      state,
      { payload: { screenName, name, count } }: PayloadAction<SetBadgeCount>
    ) => {
      const participant = state.participants?.[screenName]
      if (participant) {
        const [badges, countDiff] = setCount({
          currentBadges: participant.badges,
          name,
          count,
        })
        // update the total badges (diff can be negative and result in an increase here)
        state.totalBadges -= countDiff

        // remove from object entirely if the current badge count matches the diff
        if (state.allBadgesCounts?.[name] === countDiff) {
          delete state.allBadgesCounts[name]
          // or subtract the diff
        } else {
          state.allBadgesCounts[name] -= countDiff
        }
        state.participants[screenName].badges = badges.sort(
          (a, b) => b.count - a.count
        ) // sort the badges here instead within components
      }
    },
    clearAllBadges: (state) => {
      const clearedParticipants: Participants = {}

      Object.keys(state.participants).forEach((screenName) => {
        clearedParticipants[screenName] = {
          ...state.participants[screenName],
          badges: [],
        }
      })
      state.totalBadges = 0
      state.allBadgesCounts = initialState.allBadgesCounts
      state.participants = clearedParticipants
    },
  },
  // allows values to be initial state for the session reducer
  extraReducers: (builder) => {
    // this goes first to obtain any info from the s3 bucket
    builder.addMatcher(
      appApi.endpoints.getHostSettings.matchFulfilled,
      (state, { payload: { activeSession } }) => {
        if (activeSession) {
          state = activeSession
        }
        return state
      }
    )
    // if we create a session, set the entire response
    builder.addMatcher(
      appApi.endpoints.createSession.matchFulfilled,
      (_state, { payload }) => {
        return payload
      }
    )
    // if we delete a session, reset the state
    builder.addMatcher(
      appApi.endpoints.deleteSession.matchFulfilled,
      (state, { payload: { wasActiveSession } }) => {
        if (wasActiveSession) {
          state = initialState
        }
        return state
      }
    )
    // if we end a session, reset the state
    builder.addMatcher(appApi.endpoints.endActiveSession.matchFulfilled, () => {
      return initialState
    })
    builder.addMatcher(
      appApi.endpoints.updateGroup.matchFulfilled,
      (state, { payload: { newGroupId, oldGroupId } }) => {
        // if the old group we had matches the changed one and it was changed to an already existing group
        if (state.groupId === oldGroupId && newGroupId) {
          state.groupId = newGroupId
        }
      }
    )
  },
})

export const {
  addParticipants,
  incrementBadge,
  clearAllBadges,
  setBadgeCount,
  setName,
  setGroupId,
} = sessionSlice.actions

export const selectSessionName = (state: RootState) => state.session.name
export const selectSessionId = (state: RootState) => state.session.id
export const selectSessionGroupId = (state: RootState) => state.session.groupId
export const selectSession = (state: RootState) => state.session
export const selectSessionParticipants = (state: RootState) =>
  state.session.participants
export const selectParticipantBadges = createSelector(
  selectSessionParticipants,
  (state: RootState) => state.user.screenName,
  (participants, screenName) => participants?.[screenName || '']?.badges || []
)

export default sessionSlice
